import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Layout from '../components/Layout'
import CopyButton from '../components/CopyButton'
import { addToHistory } from '../utils/history'

type IndentSize = 2 | 4 | 'tab'

function JsonFormatter() {
  const { t } = useTranslation()
  const [input, setInput] = useState('')
  const [output, setOutput] = useState('')
  const [error, setError] = useState('')
  const [indentSize, setIndentSize] = useState<IndentSize>(2)

  const saveToHistory = useCallback((inputVal: string, outputVal: string) => {
    if (inputVal && outputVal) {
      addToHistory({
        tool: 'json-formatter',
        toolName: 'JSON Formatter',
        input: inputVal.substring(0, 500),
        output: outputVal.substring(0, 500),
      })
    }
  }, [])

  const getIndent = (): string | number => {
    if (indentSize === 'tab') return '\t'
    return indentSize
  }

  const formatJson = (json: string) => {
    setInput(json)
    setError('')
    
    if (!json.trim()) {
      setOutput('')
      return
    }

    try {
      const parsed = JSON.parse(json)
      const formatted = JSON.stringify(parsed, null, getIndent())
      setOutput(formatted)
      saveToHistory(json, formatted)
    } catch (err) {
      if (err instanceof SyntaxError) {
        const match = err.message.match(/position (\d+)/)
        if (match) {
          const position = parseInt(match[1])
          const lines = json.substring(0, position).split('\n')
          const line = lines.length
          const column = lines[lines.length - 1].length + 1
          setError(`${t('toolPages.jsonFormatter.parseError')}: ${err.message} (${t('toolPages.jsonFormatter.line')} ${line}, ${t('toolPages.jsonFormatter.column')} ${column})`)
        } else {
          setError(`${t('toolPages.jsonFormatter.parseError')}: ${err.message}`)
        }
      } else {
        setError(t('toolPages.jsonFormatter.unknownError'))
      }
      setOutput('')
    }
  }

  const minifyJson = () => {
    setError('')
    
    const source = input.trim() || output.trim()
    if (!source) {
      setError(t('toolPages.jsonFormatter.noJsonToMinify'))
      return
    }

    try {
      const parsed = JSON.parse(source)
      const minified = JSON.stringify(parsed)
      setOutput(minified)
      saveToHistory(source, minified)
      if (!input.trim()) {
        setInput(source)
      }
    } catch (err) {
      if (err instanceof SyntaxError) {
        setError(`${t('toolPages.jsonFormatter.parseError')}: ${err.message}`)
      } else {
        setError(t('toolPages.jsonFormatter.unknownError'))
      }
    }
  }

  const handleFormat = () => {
    formatJson(input || output)
  }

  const handleClear = () => {
    setInput('')
    setOutput('')
    setError('')
  }

  const loadSample = () => {
    const sample = {
      "name": "AllinOneDecoder",
      "version": "1.0.0",
      "description": "올인원 텍스트 & 데이터 변환 도구",
      "features": [
        "URL Encode/Decode",
        "Base64",
        "JSON Formatter",
        "JWT Decoder",
        "QR Code",
        "UUID Generator",
        "Hash Generator"
      ],
      "settings": {
        "darkMode": true,
        "language": "ko",
        "autoFormat": false
      },
      "stats": {
        "users": 1000,
        "conversions": 50000
      }
    }
    const sampleStr = JSON.stringify(sample)
    setInput(sampleStr)
    formatJson(sampleStr)
  }

  const getStats = () => {
    if (!output) return null
    
    try {
      const parsed = JSON.parse(output)
      const keys = JSON.stringify(parsed).match(/"[^"]+"\s*:/g)?.length || 0
      const arrays = JSON.stringify(parsed).match(/\[/g)?.length || 0
      const objects = JSON.stringify(parsed).match(/\{/g)?.length || 0
      
      return { keys, arrays, objects, size: output.length }
    } catch {
      return null
    }
  }

  const stats = getStats()

  return (
    <Layout showBackButton title={t('toolPages.jsonFormatter.title')}>
      <div className="tool-container">
        <p className="tool-description">
          {t('toolPages.jsonFormatter.description')}
        </p>

      {/* Controls */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('toolPages.jsonFormatter.indent')}:</span>
          <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
            {([2, 4, 'tab'] as IndentSize[]).map((size) => (
              <button
                key={size}
                onClick={() => {
                  setIndentSize(size)
                  if (output) {
                    try {
                      const parsed = JSON.parse(output)
                      setOutput(JSON.stringify(parsed, null, size === 'tab' ? '\t' : size))
                    } catch {
                      // ignore
                    }
                  }
                }}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  indentSize === size
                    ? 'bg-primary-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {size === 'tab' ? 'Tab' : `${size} ${t('toolPages.jsonFormatter.spaces')}`}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-2">
          <button onClick={handleFormat} className="btn-primary">
            {t('common.format')}
          </button>
          <button onClick={minifyJson} className="btn-secondary">
            {t('common.minify')}
          </button>
          <button onClick={loadSample} className="btn-outline">
            {t('common.sample')}
          </button>
          <button onClick={handleClear} className="btn-secondary">
            {t('common.clear')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="card">
          <div className="flex justify-between items-center mb-3">
            <h2 className="section-title">{t('common.input')} (JSON)</h2>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {input.length > 0 ? `${input.length}` : ''}
            </span>
          </div>
          <textarea
            value={input}
            onChange={(e) => formatJson(e.target.value)}
            placeholder='{"key": "value"}'
            className="textarea h-80 scrollbar-thin"
            spellCheck={false}
          />
          {error && (
            <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="error-message flex items-start gap-2">
                <span>⚠️</span>
                <span>{error}</span>
              </p>
            </div>
          )}
        </div>

        {/* Output Section */}
        <div className="card">
          <div className="flex justify-between items-center mb-3">
            <h2 className="section-title">{t('common.output')}</h2>
            <CopyButton text={output} />
          </div>
          <textarea
            value={output}
            readOnly
            placeholder={t('common.output') + '...'}
            className="textarea h-80 bg-gray-50 dark:bg-gray-900 scrollbar-thin"
            spellCheck={false}
          />
          
          {/* Stats */}
          {stats && (
            <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
              <span>📊 {t('toolPages.jsonFormatter.keys')}: {stats.keys}</span>
              <span>📦 {t('toolPages.jsonFormatter.objects')}: {stats.objects}</span>
              <span>📋 {t('toolPages.jsonFormatter.arrays')}: {stats.arrays}</span>
              <span>💾 {t('toolPages.jsonFormatter.size')}: {stats.size} bytes</span>
            </div>
          )}
        </div>
      </div>
      </div>
    </Layout>
  )
}

export default JsonFormatter
